/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';

import ExerciseFilterGroup from './ExerciseFilterGroup';
import SCAnalysisTable from './SCAnalysisTable';
import SCAnalysisTableNoData from './SCAnalysisTableNoData';
import ForcePlateDatePicker from '../../../force-plate-dashboard/components/presentational/ForcePlateDatePicker';
import ForcePlateDropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import {
  fetchSCDashData,
  setEndDateCal,
  setEndDateData,
  setGroup,
  setSelectedAthleteGroup,
  setSelectedAthletePerformance,
  setStartDateCal,
  setStartDateData,
} from '../../ducks/sportCoachDashboardActions';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const ColorNote = styled('div')`
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: normal;
`;

const DashboardContainer = styled('div')`
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  margin: 15px;
  padding: 16px;
  position: relative;
`;

const DateOptionsContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  .downloadBtn {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 35px;
    margin-left: 10px;
    padding: 3px;
    width: 35px;
  }
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const NoDataModal = styled('div')`
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  color: #424242;
  font-family: 'Nunito Sans';
  font-size: 19px;
  height: 119px;
  left: 50%;
  margin: -45px 0px 0px -182px;
  padding: 15px;
  position: absolute;
  text-align: center;
  top: 50%;
  width: 400px;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const AnalysisComponent = () => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const {
    athleteList,
    cardData,
    dateType,
    endDateCal,
    endDateData,
    exerciseFiltersGroup,
    exercises,
    group,
    groups,
    maxType,
    selectedAthleteGroup,
    startDateCal,
    startDateData,
    timingGateExercises,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const { isGroupAnalysisLoading } = useSelector((state) => state.sportCoachDashboard.ui);

  const [columnHeaderHeight, setColumnHeaderHeight] = useState(null);

  const dispatch = useDispatch();

  const containerWidth = 1000;

  const hasData = cardData.length > 0;
  const exerIds = useMemo(
    () => (exercises.length > 0 ? exercises.map((item) => item.id) : []),
    [exercises],
  );
  const timingGateExerciseDetails = timingGateExercises.length > 0 ? timingGateExercises : [];

  const setAthlete = (option) => {
    if (option.fullName === 'View Group') {
      dashboardTracker(
        'Sport Coach Dashboard',
        'Group Analysis - Athlete Picker',
        'Athlete selection reset',
      );
      dispatch(setSelectedAthleteGroup(null, false));
    } else {
      dashboardTracker(
        'Sport Coach Dashboard',
        'Group Analysis - Athlete Picker',
        'Athlete selected',
      );
      dispatch(setSelectedAthleteGroup(option, true));
    }
  };

  const setEndDate = (date) => {
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Date Picker',
      'End date selected',
    );
    const updatedEndDateData = moment(date).format('YYYY-MM-DD');
    const updatedEndDateCal = moment(date);
    const submissionValues = {
      startDate: startDateData,
      endDate: updatedEndDateData,
      groupId: group?.id,
      exerciseIds: exerIds,
      dateType,
      maxType,
      timingGateExercises: timingGateExerciseDetails,
    };
    batch(() => {
      dispatch(setEndDateData(updatedEndDateData));
      dispatch(setEndDateCal(updatedEndDateCal));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          submissionValues,
        ),
      );
    });
  };

  const setSelectedGroup = (groupOption) => {
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Group Picker',
      'Group selected',
    );
    const submissionValues = {
      startDate: startDateData,
      endDate: endDateData,
      groupId: groupOption?.id,
      exerciseIds: exerIds,
      dateType,
      maxType,
      timingGateExercises: timingGateExerciseDetails,
    };
    batch(() => {
      dispatch(setGroup(groupOption));
      dispatch(setSelectedAthleteGroup(null, false));
      dispatch(setSelectedAthletePerformance(null, false));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          submissionValues,
        ),
      );
    });
  };

  const setStartDate = (date) => {
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Date Picker',
      'Start date selected',
    );
    const updatedStartDateData = moment(date).format('YYYY-MM-DD');
    const updatedStartDateCal = moment(date);
    const submissionValues = {
      startDate: updatedStartDateData,
      endDate: endDateData,
      groupId: group?.id,
      exerciseIds: exerIds,
      dateType,
      maxType,
      timingGateExercises: timingGateExerciseDetails,
    };
    batch(() => {
      dispatch(setStartDateData(updatedStartDateData));
      dispatch(setStartDateCal(updatedStartDateCal));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          submissionValues,
        ),
      );
    });
  };

  const athleteDropdownList = useMemo(() => {
    if (!hasData || athleteList.length === 0) return [];
    return [{ fullName: 'View Group', userId: null }, ...athleteList];
  }, [hasData, athleteList]);

  const groupAverageData = useMemo(() => {
    if (!hasData) return [];

    return exerciseFiltersGroup.map((exerName) => {
      const exercise = cardData.find((exer) => exer.metricName === exerName);

      if (exercise) {
        return {
          color: exercise.color,
          metricName: exercise.metricName,
          newestAverage: exercise.newestAverage,
          oldestAverage: exercise.oldestAverage,
          percentChange: exercise.percentChange,
        };
      }

      return {
        color: null,
        metricName: exerName,
        newestAverage: null,
        oldestAverage: null,
        percentChange: null,
      };
    });
  }, [hasData, exerciseFiltersGroup, cardData]);

  const manipulatedData = useMemo(() => {
    if (!hasData) return [];

    const shouldIncludeAthlete = (athlete) => !selectedAthleteGroup
      || selectedAthleteGroup.fullName === 'View Group'
      || (selectedAthleteGroup.fullName !== 'View Group'
        && selectedAthleteGroup.userId === athlete.userId);

    const buildAthleteResults = (athlete) => exerciseFiltersGroup.map((exerciseName) => {
      const exerciseData = cardData.find(
        (exercise) => exercise.metricName === exerciseName,
      );

      if (!exerciseData) {
        return {
          color: null,
          exerName: exerciseName,
          newestMax: null,
          oldestMax: null,
          percentChange: null,
        };
      }

      const athleteExerciseData = exerciseData.athleteData.find(
        (resultData) => resultData.userId === athlete.userId,
      );

      if (!athleteExerciseData) {
        return {
          color: null,
          exerName: exerciseName,
          newestMax: null,
          oldestMax: null,
          percentChange: null,
        };
      }

      return {
        color: athleteExerciseData.color,
        exerName: exerciseName,
        newestMax: athleteExerciseData.newestMax,
        oldestMax: athleteExerciseData.oldestMax,
        percentChange: athleteExerciseData.percentChange,
      };
    });

    return athleteList.filter(shouldIncludeAthlete).map((athlete) => ({
      fullName: athlete.fullName,
      pic: athlete.pic,
      results: buildAthleteResults(athlete),
      userId: athlete.userId,
    }));
  }, [hasData, athleteList, selectedAthleteGroup, exerciseFiltersGroup, cardData]);

  const renderNoDataContent = () => (
    <>
      <SCAnalysisTableNoData
        containerWidth={containerWidth}
        dataRows={['', '', '', '', '']}
        rowIsScrollable
        setColumnHeaderHeight={setColumnHeaderHeight}
      />
      <NoDataModal>
        <ModalTitle>
          {!group && !hasData ? 'Select a Group' : 'No Data Available'}
        </ModalTitle>
        <ModalMessage>
          {!group && !hasData
            ? 'Select a group using the dropdown above to get started'
            : 'There is no data available, please adjust your selection above'}
        </ModalMessage>
      </NoDataModal>
    </>
  );

  const renderOptionsHeader = () => (
    <OptionsHeader>
      <MenuOptionsContainer>
        <ForcePlateDropDown
          defaultOption={group || {}}
          headerWidthPx={175}
          label='name'
          list={groups}
          placeholder='Select a Group'
          readOnly
          selectItemFunction={setSelectedGroup}
        />
        {hasData && (
          <ForcePlateDropDown
            defaultOption={selectedAthleteGroup || {}}
            headerWidthPx={175}
            label='fullName'
            list={athleteDropdownList}
            placeholder='Select an Athlete'
            selectItemFunction={setAthlete}
          />
        )}
        <ExerciseFilterGroup />
      </MenuOptionsContainer>
      <DateOptionsContainer>
        <ForcePlateDatePicker
          endDate={endDateCal.toDate()}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDateCal.toDate()}
        />
      </DateOptionsContainer>
    </OptionsHeader>
  );

  const renderTableContent = () => {
    if (isGroupAnalysisLoading) {
      return <Spinner large />;
    }

    if (!hasData) {
      return renderNoDataContent();
    }

    if (manipulatedData.length > 0) {
      return (
        <SCAnalysisTable
          containerWidth={containerWidth}
          dataRows={manipulatedData}
          groupAverageData={groupAverageData}
          rowIsScrollable
          setColumnHeaderHeight={setColumnHeaderHeight}
        />
      );
    }

    return null;
  };

  return (
    <DashboardContainer>
      {renderOptionsHeader()}
      <ColorNote>
        <span style={{ fontWeight: 'bold' }}>NOTE: </span>
        The column background color is determined by the amount of percent
        change. The percent change column will be red if performance drops more
        than 3% and green if there is any improvement in performance.
      </ColorNote>
      {renderTableContent()}
    </DashboardContainer>
  );
};

export default AnalysisComponent;
